import { createSlice } from '@reduxjs/toolkit';

import { QuoteEntry } from '../../interfaces';
import { DownloadRFXRequestAttachmentOutput } from '../../interfaces/RFXBackendModel';
import { RFXRequestForm, RFXRequestState } from '../../interfaces/RFXRequestForm';
import { RootState } from '../../store';

interface RFXRequestFromState {
  currentRFXId: string;
  currentRound: number;
  quoteEntries: QuoteEntry[];
  documentEntries: { [key: string]: DownloadRFXRequestAttachmentOutput };
  currentRFXRequestForm: RFXRequestForm;
  isPreRoundQuoteCreated: boolean;
  isPreRoundDocumentCreated: boolean;
  isExistingRFXRequest: boolean;
}

const currentRFXRequestForm: RFXRequestForm = {
  state: RFXRequestState.DRAFT,
  round: 1,
  rfxRequestVersion: 1,
  generalInformation: {
    userRfxId: '',
    fullRfxId: '',
    rfxType: '',
    rfxName: '',
    beginDate: '',
    beginTime: '',
    dueDate: '',
    dueTime: '',
    needByDate: '',
    needByTime: '',
    organization: {
      label: ''
    },
    department: {
      label: ''
    },
    category: {
      label: ''
    },
    project: '',
    region: {
      label: ''
    },
    currency: {
      label: ''
    },
    quoteTemplateType: {
      label: ''
    },
    rfxLineItemClassId: '',
    businessUnit: {
      label: ''
    },
    eventSubType: { option: { label: '' } },
    overview: '',
    processDetails: ''
  },
  quotes: {
    quoteEntries: [],
    commodityInformation: undefined,
    quoteTemplateGenerationStatus: undefined
  },
  suppliers: {
    supplierEntries: [],
    existingSupplierContactsMap: {}
  },
  'co-managers': { coManagerEntries: [] },
  documents: {
    documentEntries: []
  },
  review: {},
  trackingInformation: {}
};

const initialState: RFXRequestFromState = {
  currentRFXId: '',
  currentRound: 1,
  quoteEntries: [],
  documentEntries: {},
  currentRFXRequestForm,
  isPreRoundQuoteCreated: false,
  isPreRoundDocumentCreated: false,
  isExistingRFXRequest: false
};

const rfxRequestFormSlice = createSlice({
  name: 'manageRFXRequest',
  initialState,
  reducers: {
    setCurrentRFXId(state, action) {
      state.currentRFXId = action.payload;
    },
    setCurrentRound(state, action) {
      state.currentRound = action.payload;
    },
    setQuoteEntries(state, action) {
      state.quoteEntries = action.payload;
    },
    setDocumentEntries(state, action) {
      state.documentEntries = action.payload;
    },
    setCurrentRFXRequestForm(state, action) {
      state.currentRFXRequestForm = action.payload;
    },
    setIsPreRoundQuoteCreated(state, action) {
      state.isPreRoundQuoteCreated = action.payload;
    },
    setIsPreRoundDocumentCreated(state, action) {
      state.isPreRoundDocumentCreated = action.payload;
    },
    setIsExistingRFXRequest(state, action) {
      state.isExistingRFXRequest = action.payload;
    }
  }
});

export const {
  setCurrentRFXId,
  setCurrentRFXRequestForm,
  setCurrentRound,
  setQuoteEntries,
  setDocumentEntries,
  setIsPreRoundQuoteCreated,
  setIsPreRoundDocumentCreated,
  setIsExistingRFXRequest
} = rfxRequestFormSlice.actions;

export const selectCurrentRFXId = (state: RootState) => state.manageRFXRequest.currentRFXId;

export const selectCurrentRound = (state: RootState) => state.manageRFXRequest.currentRound;

export const selectQuoteEntries = (state: RootState): QuoteEntry[] => state.manageRFXRequest.quoteEntries;

export const selectDocumentEntries = (state: RootState): { [key: string]: DownloadRFXRequestAttachmentOutput } =>
  state.manageRFXRequest.documentEntries;

export const selectCurrentRFXRequestForm = (state: RootState) => state.manageRFXRequest.currentRFXRequestForm;

export const selectIsPreRoundQuoteCreated = (state: RootState) => state.manageRFXRequest.isPreRoundQuoteCreated;

export const selectIsPreRoundDocumentCreated = (state: RootState) => state.manageRFXRequest.isPreRoundDocumentCreated;

export const selectIsExistingRFXRequest = (state: RootState) => state.manageRFXRequest.isExistingRFXRequest;

export default rfxRequestFormSlice.reducer;
